/**
 * PwaHints.tsx —— PWA 轻量状态提示
 *
 * 第 16 轮按用户要求移除全部「安装引导」横幅（iOS「添加到主屏，随时开局」+ 桌面
 * 「把牌局装成桌面应用 / 下载到电脑游玩」），避免打扰开局。此处只保留纯状态反馈：
 *   · OfflineBadge —— 断网时顶部中性 chip「离线中 · 本地对战不受影响」（role=status 播报一次）；
 *   · 首次具备离线能力时 Toast 一次「已可离线畅玩」，之后不复现。
 *
 * 全程不含禁词；文案沿用设计定稿的脱敏措辞（「牌局 / 对战」）。
 */
import { useEffect, useState } from 'react';
import { pushToast } from '../state/toasts';
import { useLetway } from './hooks';

const LS_OFFLINE_READY = 'riverside.pwa.offlineReadyToast';

export function PwaHints() {
  const [online, setOnline] = useState<boolean>(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  /**
   * 二期 FR-E2 规则二 / FR-E3：关键/打断浮层在场（mp-confirm/sync/drawer/review 或单机结算）时进入 pwa 让位态。
   * 安装条（.pwa-hint）本项目已于第 16 轮移除、此处不渲染，让位表现为断网 chip 让位；
   * 断网 chip 采「下移不隐藏」保证断网可见性（设计 §9），移到不与关键浮层 CTA/关键读数相交的安全区并缩小。
   */
  const { pwaYield } = useLetway();

  // 在线/离线状态：断网 chip 与恢复自动消失
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  // 首次具备离线能力 → 只 Toast 一次
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    let cancelled = false;
    navigator.serviceWorker.ready
      .then(() => {
        if (cancelled) return;
        try {
          if (localStorage.getItem(LS_OFFLINE_READY) === '1') return;
          localStorage.setItem(LS_OFFLINE_READY, '1');
        } catch {
          /* 隐私模式禁用存储时，静默跳过（不影响可玩性） */
        }
        pushToast('已可离线畅玩', 'success');
      })
      .catch(() => {
        /* SW 未注册（开发态 / 首次未联网）时无离线能力，静默 */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      {!online ? (
        <div
          className={`pwa-offline${pwaYield ? ' pwa-offline--yield' : ''}`}
          role="status"
          aria-label="当前离线，本地对战不受影响"
          data-yield={pwaYield ? '1' : undefined}
        >
          离线中 · 本地对战不受影响
        </div>
      ) : null}
    </>
  );
}
